"use client"

import { Images } from "@/constants/images"
import { useTimelineNavigation } from "@/hook/useAboutUs"
import { TimelineNavigationProps } from "@/types/types"
import Image from "next/image"
import "swiper/css"
import "swiper/css/navigation"
import { Navigation } from "swiper/modules"
import { Swiper, SwiperSlide } from "swiper/react"
import styles from "./TimelineNavigation.module.css"

export const TimelineNavigation = ({
  years,
  activeIndex,
  onYearClick,
  onPrevClick,
  onNextClick,
  canGoPrev,
  canGoNext,
}: TimelineNavigationProps) => {
  const {
    dragging,
    dragButtonRef,
    swiperContainerRef,
    enableLoop,
    displayYears,
    slidesPerView,
    loopAdditionalSlides,
    handleSlideChange,
    handlePrevClick,
    handleNextClick,
    handleMouseDown,
    handleSwiperInit,
  } = useTimelineNavigation(years, activeIndex, onYearClick, canGoPrev, canGoNext)

  return (
    <div className="flex items-center w-full pb-0 sm:pb-[2.875rem]">
      <button
        onClick={handlePrevClick}
        disabled={!canGoPrev}
        className={`flex items-center justify-center transition-opacity relative w-5 h-[15px] sm:w-[2.5rem] sm:h-[1.875rem] flex-shrink-0 ${
          canGoPrev ? "opacity-100 sm:hover:opacity-80 cursor-pointer" : "opacity-30 cursor-not-allowed"
        }`}
        aria-label="Previous year"
      >
        <Image src={Images.arrowLeftSwiper} alt="Previous" fill className="object-contain" />
      </button>

      <div ref={swiperContainerRef} className={`max-w-[550px] overflow-hidden mx-auto ${styles.timelineNavContainer}`}>
        <Swiper
          modules={[Navigation]}
          spaceBetween={8}
          slidesPerView={slidesPerView}
          slidesPerGroup={1}
          speed={500}
          loop={enableLoop}
          loopAdditionalSlides={loopAdditionalSlides}
          allowTouchMove={false}
          grabCursor={false}
          onSwiper={handleSwiperInit}
          onSlideChange={handleSlideChange}
          className="timeline-nav-swiper"
          breakpoints={{
            320: {
              slidesPerView: slidesPerView,
              spaceBetween: 8,
            },
            768: {
              slidesPerView: slidesPerView,
              spaceBetween: 8,
            },
          }}
        >
          {displayYears.map((year, index) => {
            const actualIndex = index % years.length
            const isActive = actualIndex === activeIndex
            return (
              <SwiperSlide key={`${year}-${index}`}>
                <div className="flex items-center justify-center h-full w-full flex-nowrap">
                  <div className="h-px w-[34px] flex-shrink-0 bg-[#E5E3E3] transition-colors duration-300 sm:block" />
                  <button
                    ref={index === activeIndex ? dragButtonRef : null}
                    onMouseDown={e => handleMouseDown(e, actualIndex)}
                    onClick={e => {
                      // onClick được xử lý trong handleMouseUp để có animation mượt mà
                      // Không cần gọi onYearClick ở đây vì handleMouseUp đã xử lý
                    }}
                    className={`sm:p-2 p-0 transition-all duration-300 whitespace-nowrap flex-shrink-0 flex items-center justify-center text-center min-w-[60px] ${
                      isActive
                        ? "text-2xl font-bold text-[#18AF4E]"
                        : "text-base leading-[1.5rem] font-normal text-[#333333]"
                    } ${dragging ? "cursor-grabbing select-none" : "cursor-grab"}`}
                    aria-label={`Go to year ${year}`}
                    style={{
                      userSelect: dragging ? "none" : "auto",
                      pointerEvents: "auto",
                    }}
                  >
                    {year}
                  </button>
                  <div className="hidden sm:block h-px w-[25px] flex-shrink-0 bg-[#E5E3E3] transition-colors duration-300" />
                </div>
              </SwiperSlide>
            )
          })}
        </Swiper>
      </div>

      <button
        onClick={handleNextClick}
        disabled={!canGoNext}
        className={`flex items-center justify-center transition-opacity relative w-5 h-[15px] sm:w-[2.5rem] sm:h-[1.875rem] flex-shrink-0 ml-auto ${
          canGoNext ? "opacity-100 sm:hover:opacity-80 cursor-pointer" : "opacity-30 cursor-not-allowed"
        }`}
        aria-label="Next year"
      >
        <Image src={Images.arrowRightSwiper} alt="Next" fill className="object-contain" />
      </button>
    </div>
  )
}
